import React, {
	useCallback,
	useImperativeHandle,
	forwardRef,
	useMemo,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import { TooltipComponent, MarkLineComponent, GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { Box, xcss, Stack } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import type { ChartData } from '../../common/summary/types.tsx';
import { getFormattedLabel } from '../../common/summary/utils/index.tsx';
import type { Point } from '../../common/types.tsx';
import {
	getGridConfiguration,
	getTooltipConfiguration,
	getXAxisConfiguration,
	getYAxisConfiguration,
} from './chart-configuration/index.tsx';
import { useChartEvents } from './chart-events/index.tsx';
import type { EChartsEvent } from './chart-events/types.tsx';
import { formatTooltip } from './tooltip/index.tsx';

// Register the required components
echarts.use([TooltipComponent, MarkLineComponent, GridComponent, LineChart, SVGRenderer]);

export type RefInternal = {
	highlightPoint: (arg1: EChartsEvent) => void;
	downplayPoint: () => void;
};

type Ref = {
	current: RefInternal | null;
};

type Props = {
	source: ChartData;
} & {
	ref?: Ref;
	onPointSelect?: (arg1: EChartsEvent) => void;
};

// setting this in css does not work with echarts-for-react v3, as it sets `height` to 300px if it is not provided in `style` prop
const echartsForReactStyle = {
	width: '100%',
	height: '100%',
};

export default forwardRef<RefInternal, Props>(({ source, onPointSelect }, ref) => {
	const { colorMode } = useThemeObserver();
	const { searchBy, axisLabels, chartSeries, yAxisMinMax } = source;
	const intl = useIntl();

	const formatter = useCallback(
		(points: Point[]) => formatTooltip(intl, points, searchBy),
		[intl, searchBy],
	);

	const yAxisFormatterType = useMemo(
		() => chartSeries.length && chartSeries[0].data.length && chartSeries[0].data[0].valueType,
		[chartSeries],
	);

	const yAxisFormatter = useCallback(
		(axisValue: string): string => {
			if (yAxisFormatterType)
				return getFormattedLabel(yAxisFormatterType, axisValue, intl.formatMessage);
			return axisValue;
		},
		[intl.formatMessage, yAxisFormatterType],
	);

	const option = useMemo(
		() => ({
			animation: true,
			animationDuration: 300,
			xAxis: getXAxisConfiguration(axisLabels),
			yAxis: getYAxisConfiguration({ yAxisFormatter, minMaxConfig: yAxisMinMax }),
			grid: getGridConfiguration(),
			tooltip: getTooltipConfiguration(formatter),
			series: chartSeries,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[axisLabels, chartSeries, formatter, yAxisFormatter, yAxisMinMax, colorMode],
	);

	const { initChartEventsOnMount, highlightPoint, downplayPoint, refreshOptions } = useChartEvents({
		onPointSelect,
		source,
	});

	useImperativeHandle(ref, () => ({
		highlightPoint,
		downplayPoint,
	}));

	useEffect(() => {
		refreshOptions(option);
	}, [option, refreshOptions]);

	return (
		<JsmReportsEcharts>
			<ReactEchartsCore
				echarts={echarts}
				onChartReady={initChartEventsOnMount}
				option={option}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={echartsForReactStyle}
			/>
		</JsmReportsEcharts>
	);
});

type AspectRatioFallbackProps = {
	children: ReactNode;
};

const AspectRatioFallback = ({ children }: AspectRatioFallbackProps) => (
	<Box as="div" xcss={aspectRatioFallbackStyles}>
		{children}
	</Box>
);

type JsmReportsEchartsProps = {
	children: ReactNode;
};

const JsmReportsEcharts = ({ children }: JsmReportsEchartsProps) => {
	const [supportsAspectRatio, setSupportsAspectRatio] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined' && 'CSS' in window && 'supports' in CSS) {
			setSupportsAspectRatio(CSS.supports('aspect-ratio', '1 / 1'));
		}
	}, []);

	const jsmReportsEchartsFallbackStyles = xcss({
		position: 'relative',
	});

	const combinedStyles = supportsAspectRatio
		? jsmReportsEchartsBaseStyles
		: {
				...jsmReportsEchartsBaseStyles,
				...jsmReportsEchartsFallbackStyles,
			};

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		<Box xcss={combinedStyles}>
			{!supportsAspectRatio ? (
				<Stack alignBlock="center">
					<AspectRatioFallback>{children}</AspectRatioFallback>
				</Stack>
			) : (
				children
			)}
		</Box>
	);
};

const aspectRatioFallbackStyles = xcss({
	height: '100%',
	position: 'absolute',
	top: 'space.0',
	right: 'space.0',
	bottom: 'space.0',
	left: 'space.0',
});

const jsmReportsEchartsBaseStyles = xcss({
	width: '100%',
	aspectRatio: '3 / 1',
});
